/**
 * Member detail response from GET /client/{mcode}/subclients/{ccode}/members/{subId}
 * (UI route: `/group/{mcode}/subgroup/{ccode}/members/{subId}`.)
 * Normalized camelCase shapes; use normalizeMemberDetailResponse when API uses PascalCase
 * or flat root-level contact fields (see sample response in HR portal docs).
 */

import { isMaskedMemberSsn, ssnDigitsForMemberEditBaseline } from '../utils/masked-field.util';

export type MemberProductStatusKind = string;

export interface MemberDetailProductDto {
  /** Present when API sends it; required for cancel product PATCH URL. */
  memberProductId?: number;
  /**
   * Client catalog id when present (e.g. add product).
   */
  clientProductId?: number;
  /**
   * Product id from the API — used to match duplicate member-product rows (e.g. Coverage Ended + other status).
   */
  productId?: number;
  productName: string;
  productType: string;
  status: MemberProductStatusKind;
  /** Subgroup / company code on the product (API may send `CCode`). */
  cCode?: string;
  /** Subgroup / company name on the product (API may send `CName`). */
  cName?: string;
  effectiveDate?: string;
  expirationDate?: string;
  cancelDate?: string;
  memberRate?: number;
  clientRate?: number;
  totalRate?: number;
}

export interface MemberDetailDependentDto {
  /** Present when API returns it; required for remove/edit dependent URL. */
  dependentId?: number;
  fullName: string;
  /** Individual name fields — populated when API returns them; used to pre-fill the edit form. */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  relationship: string;
  age?: number;
  status: string;
  dateOfBirth?: string;
}

export interface MemberDetailLawFirmDto {
  name: string;
  code: string;
  phone?: string;
  address?: string;
  startDate?: string;
  endDate?: string;
}

export interface MemberDetailContactDto {
  dateOfBirth?: string;
  primaryEmail?: string;
  secondaryEmail?: string;
  mobilePhone?: string;
  homePhone?: string;
  workPhone?: string;
  /** Single-line formatted address if API returns it */
  addressLine?: string;
  street?: string;
  suite?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

/**
 * Full member detail payload for the HR portal detail page.
 */
export interface MemberDetailDto {
  subId: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  employeeId?: string;
  creationDate?: string;
  ssn?: string;
  /** Server-resolved: SSN may be displayed and edited for this member. */
  isSsnEditable?: boolean;
  companyName?: string;
  /** When true, the HR portal must not offer profile edit (API: `IsProfileReadOnly`). */
  isProfileReadOnly?: boolean;
  /**
   * True when the sub-client has at least one HR-portal-available product
   * (API: `HasHrPortalAvailableProducts`). Used to show Add Product.
   */
  hasHrPortalAvailableProducts?: boolean;
  /**
   * When true, the sub-client’s open enrollment is active (same server rule as add product).
   * false when OE is not active, company enrollment dates are missing, or only a future OE exists.
   */
  isOpenEnrollmentActive?: boolean;
  /** When true, primary/secondary email are not editable (API: `AreEmailsReadOnly`). */
  areEmailsReadOnly?: boolean;
  /**
   * Member-level status for this subgroup (ccode) from the portal view — same source as the members list.
   * Prefer for summary/header; do not recompute from products.
   */
  status?: string | null;
  /**
   * Portal effective date-time for this subgroup row (aligns with {@link status}).
   * ISO 8601 when serialized as .NET DateTime.
   */
  effectiveDate?: string | null;
  /**
   * Coverage end for this member under this ccode, when applicable (API may use `CoverageExpDate`).
   * List API uses `coverageExpirationDate` on list items; same meaning.
   */
  coverageExpDate?: string | null;
  /** Company code for this row; should match route `ccode` when the load succeeds. */
  cCode?: string | null;
  /** Company display name for this row. */
  cName?: string | null;
  products: MemberDetailProductDto[];
  dependents: MemberDetailDependentDto[];
  /** Present when API nests contact; otherwise built from root-level fields. */
  contact?: MemberDetailContactDto;
}

function str(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

/** String, null, or undefined — preserves `null` from JSON for member portal fields. */
function strOrNull(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (v === undefined) return undefined;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function num(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function bool(v: unknown): boolean {
  return v === true || v === 'true';
}

/** Street line from API `address.address` */
function addressBlockToContactFields(addr: Record<string, unknown>): Partial<MemberDetailContactDto> {
  const street = str(addr['address'] ?? addr['street'] ?? addr['Address'] ?? addr['Street']);
  const city = str(addr['city'] ?? addr['City']);
  const state = str(addr['state'] ?? addr['State']);
  const zipCode = str(addr['zipCode'] ?? addr['zip'] ?? addr['ZipCode']);
  const line = [street, [city, state].filter(Boolean).join(', '), zipCode].filter(Boolean).join(', ');
  return {
    street,
    city,
    state,
    zipCode,
    addressLine: line || undefined
  };
}

function formatLawFirmAddress(addr: unknown): string | undefined {
  if (!addr || typeof addr !== 'object') return undefined;
  return addressBlockToContactFields(addr as Record<string, unknown>).addressLine;
}

function productFromRaw(raw: Record<string, unknown>): MemberDetailProductDto {
  return {
    memberProductId: num(raw['memberProductId'] ?? raw['MemberProductId']),
    clientProductId: num(raw['clientProductId'] ?? raw['ClientProductId']),
    productId: num(raw['productId'] ?? raw['ProductId']),
    productName:
      str(raw['productName'] ?? raw['name'] ?? raw['Name'] ?? raw['ProductName']) ?? '',
    productType:
      str(raw['productType'] ?? raw['type'] ?? raw['Type'] ?? raw['ProductType']) ?? '',
    status: str(raw['status'] ?? raw['Status']) ?? '',
    cCode: str(raw['cCode'] ?? raw['CCode'] ?? raw['companyCode'] ?? raw['CompanyCode']),
    cName: str(raw['cName'] ?? raw['CName'] ?? raw['companyName'] ?? raw['CompanyName']),
    effectiveDate: str(raw['effectiveDate'] ?? raw['EffectiveDate']),
    expirationDate: str(
      raw['expirationDate'] ??
        raw['ExpirationDate'] ??
        raw['coverageExpirationDate'] ??
        raw['CoverageExpirationDate']
    ),
    cancelDate: str(raw['cancelDate'] ?? raw['CancelDate']),
    memberRate: num(raw['memberRate'] ?? raw['MemberRate']),
    clientRate: num(raw['clientRate'] ?? raw['ClientRate']),
    totalRate: num(raw['totalRate'] ?? raw['TotalRate'])
  };
}

function dependentFromRaw(raw: Record<string, unknown>): MemberDetailDependentDto {
  const fn = str(raw['fullName'] ?? raw['FullName']);
  const first = str(raw['firstName'] ?? raw['FirstName']);
  const middle = str(raw['middleName'] ?? raw['MiddleName']);
  const last = str(raw['lastName'] ?? raw['LastName']);
  const fullName = fn ?? ([first, middle, last].filter(Boolean).join(' ').trim() || '—');

  return {
    dependentId: num(raw['dependentId'] ?? raw['DependentId']),
    fullName,
    firstName: first,
    middleName: middle,
    lastName: last,
    relationship: str(raw['relationship'] ?? raw['Relationship']) ?? '—',
    age: num(raw['age'] ?? raw['Age']),
    status: str(raw['status'] ?? raw['Status']) ?? '—',
    dateOfBirth: str(raw['dateOfBirth'] ?? raw['DateOfBirth'])
  };
}

function lawFirmFromRaw(raw: Record<string, unknown>): MemberDetailLawFirmDto {
  const addr = raw['address'] ?? raw['Address'];
  let addressStr: string | undefined;
  if (typeof addr === 'string') {
    addressStr = addr.trim() || undefined;
  } else if (addr && typeof addr === 'object') {
    addressStr = formatLawFirmAddress(addr);
  }

  return {
    name: str(raw['name'] ?? raw['Name']) ?? '',
    code: str(raw['code'] ?? raw['Code']) ?? '',
    phone: str(raw['phone'] ?? raw['Phone']),
    address: addressStr,
    startDate: str(raw['startDate'] ?? raw['StartDate']),
    endDate: str(raw['endDate'] ?? raw['EndDate'])
  };
}

function contactFromRaw(raw: Record<string, unknown>): MemberDetailContactDto {
  const addr = raw['address'] ?? raw['Address'];
  const fromAddr =
    addr && typeof addr === 'object' ? addressBlockToContactFields(addr as Record<string, unknown>) : {};

  return {
    dateOfBirth: str(raw['dateOfBirth'] ?? raw['DateOfBirth']),
    primaryEmail: str(raw['primaryEmail'] ?? raw['PrimaryEmail']),
    secondaryEmail: str(raw['secondaryEmail'] ?? raw['SecondaryEmail']),
    mobilePhone: str(raw['mobilePhone'] ?? raw['MobilePhone']),
    homePhone: str(raw['homePhone'] ?? raw['HomePhone']),
    workPhone: str(raw['workPhone'] ?? raw['WorkPhone']),
    addressLine: str(raw['addressLine'] ?? raw['AddressLine']) ?? fromAddr.addressLine,
    street: str(raw['street'] ?? raw['Street']) ?? fromAddr.street,
    suite: str(raw['suite'] ?? raw['Suite']),
    city: str(raw['city'] ?? raw['City']) ?? fromAddr.city,
    state: str(raw['state'] ?? raw['State']) ?? fromAddr.state,
    zipCode: str(raw['zipCode'] ?? raw['ZipCode']) ?? fromAddr.zipCode
  };
}

function contactFromRootLevel(raw: Record<string, unknown>): MemberDetailContactDto {
  const addr = raw['address'] ?? raw['Address'];
  const fromAddr =
    addr && typeof addr === 'object' ? addressBlockToContactFields(addr as Record<string, unknown>) : {};

  return {
    dateOfBirth: str(raw['dateOfBirth'] ?? raw['DateOfBirth']),
    primaryEmail: str(raw['primaryEmail'] ?? raw['PrimaryEmail']),
    secondaryEmail: str(raw['secondaryEmail'] ?? raw['SecondaryEmail']),
    mobilePhone: str(raw['mobilePhone'] ?? raw['MobilePhone']),
    homePhone: str(raw['homePhone'] ?? raw['HomePhone']),
    workPhone: str(raw['workPhone'] ?? raw['WorkPhone']),
    ...fromAddr
  };
}

function mergeContact(
  nested: MemberDetailContactDto | undefined,
  root: MemberDetailContactDto
): MemberDetailContactDto {
  return { ...nested, ...root };
}

/**
 * Maps GET .../members/{subId}/products (portal product rows).
 */
export function normalizeMemberProductsPortalResponse(body: unknown): MemberDetailProductDto[] {
  if (!Array.isArray(body)) {
    return [];
  }
  return (body as unknown[]).map((p) => productFromRaw(p as Record<string, unknown>));
}

/**
 * Maps API JSON (camelCase or PascalCase) to {@link MemberDetailDto}.
 * Supports flat root-level emails/phones/DOB and `address: { address, city, state, zipCode }`.
 */
export function normalizeMemberDetailResponse(body: unknown): MemberDetailDto {
  const raw = (body ?? {}) as Record<string, unknown>;

  const productsRaw = raw['products'] ?? raw['Products'];
  const depsRaw = raw['dependents'] ?? raw['Dependents'];
  const firmsRaw = raw['lawFirms'] ?? raw['LawFirms'];
  const contactRaw = raw['contact'] ?? raw['Contact'];

  const products = Array.isArray(productsRaw)
    ? (productsRaw as unknown[]).map((p) => productFromRaw(p as Record<string, unknown>))
    : [];

  const dependents = Array.isArray(depsRaw)
    ? (depsRaw as unknown[]).map((d) => dependentFromRaw(d as Record<string, unknown>))
    : [];

  const lawFirms = Array.isArray(firmsRaw)
    ? (firmsRaw as unknown[]).map((f) => lawFirmFromRaw(f as Record<string, unknown>))
    : [];

  const contactNested =
    contactRaw && typeof contactRaw === 'object'
      ? contactFromRaw(contactRaw as Record<string, unknown>)
      : undefined;

  const contactRoot = contactFromRootLevel(raw);
  const contact = mergeContact(contactNested, contactRoot);

  const subId = num(raw['subId'] ?? raw['SubId']) ?? 0;

  const coverageEnd = strOrNull(
    raw['coverageExpDate'] ?? raw['CoverageExpDate'] ?? raw['coverageExpirationDate'] ?? raw['CoverageExpirationDate']
  );

  return {
    subId,
    firstName: str(raw['firstName'] ?? raw['FirstName']),
    middleName: str(raw['middleName'] ?? raw['MiddleName']),
    lastName: str(raw['lastName'] ?? raw['LastName']),
    employeeId: str(raw['employeeId'] ?? raw['EmployeeId']),
    creationDate: str(raw['creationDate'] ?? raw['CreationDate']),
    ssn: str(raw['ssn'] ?? raw['SSN']),
    isSsnEditable: bool(raw['isSsnEditable'] ?? raw['IsSsnEditable']),
    companyName: str(raw['companyName'] ?? raw['CompanyName'] ?? raw['company'] ?? raw['Company']),
    isProfileReadOnly: bool(raw['isProfileReadOnly'] ?? raw['IsProfileReadOnly']),
    hasHrPortalAvailableProducts: bool(
      raw['hasHrPortalAvailableProducts'] ?? raw['HasHrPortalAvailableProducts']
    ),
    isOpenEnrollmentActive: bool(raw['isOpenEnrollmentActive'] ?? raw['IsOpenEnrollmentActive']),
    areEmailsReadOnly: bool(raw['areEmailsReadOnly'] ?? raw['AreEmailsReadOnly']),
    status: strOrNull(raw['status'] ?? raw['Status']),
    effectiveDate: strOrNull(raw['effectiveDate'] ?? raw['EffectiveDate']),
    coverageExpDate: coverageEnd,
    cCode: strOrNull(raw['cCode'] ?? raw['CCode']),
    cName: strOrNull(raw['cName'] ?? raw['CName']),
    products,
    dependents,
    contact
  };
}

function isProductStatusCoverageEnded(status: string | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'coverage ended';
}

/**
 * For the member detail products table: if two or more rows share the same `productId` and at
 * least one is not **Coverage Ended**, drop the **Coverage Ended** row(s) for that id. When
 * `productId` is missing, falls back to cCode + productName + productType.
 */
export function filterRedundantCoverageEndedMemberProducts(
  products: readonly MemberDetailProductDto[]
): MemberDetailProductDto[] {
  function dedupKey(p: MemberDetailProductDto): string {
    const id = p.productId;
    if (id != null && Number.isFinite(id)) {
      return `p:${id}`;
    }
    const code = (p.cCode ?? '').trim().toLowerCase();
    const n = (p.productName ?? '').trim().toLowerCase();
    const t = (p.productType ?? '').trim().toLowerCase();
    return `f:${code}|${n}|${t}`;
  }

  const byKey = new Map<string, MemberDetailProductDto[]>();
  for (const p of products) {
    const k = dedupKey(p);
    const g = byKey.get(k) ?? [];
    g.push(p);
    byKey.set(k, g);
  }

  const out: MemberDetailProductDto[] = [];
  for (const group of byKey.values()) {
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const hasNonCoverageEnded = group.some((p) => !isProductStatusCoverageEnded(p.status));
    if (hasNonCoverageEnded) {
      for (const p of group) {
        if (!isProductStatusCoverageEnded(p.status)) {
          out.push(p);
        }
      }
    } else {
      for (const p of group) {
        out.push(p);
      }
    }
  }
  return out;
}

// --- PATCH member basic info (partial update) ---

/** Request body for PATCH /client/{mcode}/subclients/{ccode}/members/{subId} — send only changed fields. */
export interface UpdateMemberBasicInfoDto {
  employeeId?: string | null;
  ssn?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  primaryEmail?: string | null;
  secondaryEmail?: string | null;
  homePhone?: string | null;
  workPhone?: string | null;
  mobilePhone?: string | null;
  dateOfBirth?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

/** Canonical values for diffing (aligned with {@link memberDetailToEditValues}). */
export interface MemberBasicInfoEditValues {
  firstName: string;
  middleName: string;
  lastName: string;
  /** ISO yyyy-MM-dd or null when unset */
  dateOfBirth: string | null;
  employeeId: string;
  ssn: string;
  primaryEmail: string;
  secondaryEmail: string;
  homePhone: string;
  workPhone: string;
  mobilePhone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export function normalizeMemberBasicInfoDigits(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Parses API date-of-birth strings to ISO date (yyyy-MM-dd) for comparison and forms.
 */
export function parseMemberDobToIsoDatePart(raw: string | undefined | null): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (trimmed === '') return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }
  const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (mdy) {
    const mo = mdy[1].padStart(2, '0');
    const day = mdy[2].padStart(2, '0');
    const yr = mdy[3];
    return `${yr}-${mo}-${day}`;
  }
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  return null;
}

function memberStreetLineFromContact(c: MemberDetailContactDto | undefined): string {
  if (!c) return '';
  const st = c.street?.trim();
  if (st) return st;
  const line = c.addressLine?.trim();
  if (line) {
    const first = line.split(',')[0]?.trim();
    if (first) return first;
  }
  return '';
}

function emailEquiv(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function phoneEquiv(a: string, b: string): boolean {
  return normalizeMemberBasicInfoDigits(a) === normalizeMemberBasicInfoDigits(b);
}

/**
 * Snapshot of editable fields from a loaded {@link MemberDetailDto} for diffing and form init.
 */
export function memberDetailToEditValues(m: MemberDetailDto): MemberBasicInfoEditValues {
  const c = m.contact;
  return {
    firstName: m.firstName ?? '',
    middleName: m.middleName ?? '',
    lastName: m.lastName ?? '',
    dateOfBirth: parseMemberDobToIsoDatePart(c?.dateOfBirth),
    employeeId: m.employeeId ?? '',
    ssn: ssnDigitsForMemberEditBaseline(m.ssn),
    primaryEmail: c?.primaryEmail ?? '',
    secondaryEmail: c?.secondaryEmail ?? '',
    homePhone: c?.homePhone ?? '',
    workPhone: c?.workPhone ?? '',
    mobilePhone: c?.mobilePhone ?? '',
    address: memberStreetLineFromContact(c),
    city: c?.city ?? '',
    state: c?.state ?? '',
    zipCode: c?.zipCode ?? ''
  };
}

/**
 * Builds a partial PATCH body with only changed fields. Use `""` to clear string fields per API rules.
 */
export function buildMemberBasicInfoPatch(
  before: MemberDetailDto,
  after: MemberBasicInfoEditValues
): UpdateMemberBasicInfoDto {
  const prev = memberDetailToEditValues(before);
  const out: UpdateMemberBasicInfoDto = {};

  const t = (a: string, b: string) => a.trim() === b.trim();

  if (!t(prev.firstName, after.firstName)) out.firstName = after.firstName.trim() || '';
  if (!t(prev.middleName, after.middleName)) out.middleName = after.middleName.trim() || '';
  if (!t(prev.lastName, after.lastName)) out.lastName = after.lastName.trim() || '';

  const prevDob = prev.dateOfBirth;
  const nextDob = after.dateOfBirth;
  if (prevDob !== nextDob) {
    out.dateOfBirth = nextDob == null || nextDob === '' ? '' : nextDob;
  }

  if (!t(prev.employeeId, after.employeeId)) out.employeeId = after.employeeId.trim() || '';

  const nextSsn = normalizeMemberBasicInfoDigits(after.ssn).slice(0, 9);
  if (isMaskedMemberSsn(before.ssn)) {
    if (nextSsn.length === 9) {
      out.ssn = nextSsn;
    }
  } else {
    const prevSsn = prev.ssn;
    if (prevSsn !== nextSsn) {
      out.ssn = nextSsn || '';
    }
  }

  if (!emailEquiv(prev.primaryEmail, after.primaryEmail)) {
    out.primaryEmail = after.primaryEmail.trim() || '';
  }
  if (!emailEquiv(prev.secondaryEmail, after.secondaryEmail)) {
    out.secondaryEmail = after.secondaryEmail.trim() || '';
  }

  if (!phoneEquiv(prev.homePhone, after.homePhone)) out.homePhone = after.homePhone.trim() || '';
  if (!phoneEquiv(prev.workPhone, after.workPhone)) out.workPhone = after.workPhone.trim() || '';
  if (!phoneEquiv(prev.mobilePhone, after.mobilePhone)) out.mobilePhone = after.mobilePhone.trim() || '';

  if (!t(prev.address, after.address)) out.address = after.address.trim() || '';
  if (!t(prev.city, after.city)) out.city = after.city.trim() || '';
  if (!t(prev.state, after.state)) out.state = after.state.trim() || '';
  if (!t(prev.zipCode, after.zipCode)) out.zipCode = after.zipCode.trim() || '';

  return out;
}
